import { z } from "zod";
import { isImperialUnit } from "@/utils";

/**
 * Ranges are whole kilometres, but the same value shown in miles is fractional
 * (10km = 6.21mi), so imperial operators are validated to 2 decimal places instead.
 */
const rangeSchema = (distanceUnit?: string | null) =>
  isImperialUnit(distanceUnit)
    ? z.coerce.number().min(0).max(99999).step(0.01, "Field must be a maximum of 2 decimal places.")
    : z.coerce.number().min(0).max(99999).step(1, "Field must be a whole number.");

const percentageManagementCostSchema = z.object({
  managementCostUnit: z.literal("percentage"),
  managementCost: z.coerce.number().min(0).max(9999).step(1, "Field must be a whole number."),
});

const fixedManagementCostSchema = z.object({
  managementCostUnit: z.literal("fixed"),
  managementCost: z.coerce.number().min(0).max(999999999).step(0.01, "Field must be a maximum of 2 decimal places."),
});

const managementCostSchemaUnion = z.discriminatedUnion("managementCostUnit", [percentageManagementCostSchema, fixedManagementCostSchema]);

export const createZoneRatesFormSchema = (distanceUnit?: string | null) => {
  const defaultBaseIntervalFormSchema = z.object({
    minimumRange: z.coerce.number(),
    maximumRange: rangeSchema(distanceUnit),
    intervalCost: z.coerce.number().min(0).max(999999999).step(0.01, "Field must be a maximum of 2 decimal places."),
    managementCostUnit: z.string(),
    managementCost: z.coerce.number(),
  });

  const defaultIntervalsFormSchema = z.object({
    maximumRange: rangeSchema(distanceUnit),
    intervalCost: z.coerce.number().min(0).max(999999999).step(0.01, "Field must be a maximum of 2 decimal places."),
    managementCostUnit: z.string(),
    managementCost: z.coerce.number(),
  });

  return z.object({
    zonePricingName: z.string().min(1, "Field is required and can't be empty."),
    referencePoint: z.object({
      id: z.string().min(1, "Field is required and can't be empty."),
      address: z.string().min(1, "Field is required and can't be empty."),
      coordinates: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
    }),
    pickUpPointArea: z.number(),
    baseInterval: z.intersection(defaultBaseIntervalFormSchema, managementCostSchemaUnion),
    intervals: z.array(z.intersection(defaultIntervalsFormSchema, managementCostSchemaUnion)),
  });
};

export const zoneRatesFormSchema = createZoneRatesFormSchema("km");

export type ZoneRatesFormData = z.infer<typeof zoneRatesFormSchema>;
